import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/ui/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { VenueResearchService, type VenueResearch } from '@/services/VenueResearchService';
import { ResearchForm, type ResearchFormData } from '@/components/venues/ResearchForm';
import { ResearchHistoryTable } from '@/components/venues/ResearchHistoryTable';
import { ResearchDetailsModal } from '@/components/venues/ResearchDetailsModal';
import { useNotificationStore } from '@/stores/notificationStore';
import { Sparkles } from 'lucide-react';

export function VenueResearch() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useNotificationStore();
  const [selectedResearch, setSelectedResearch] = useState<VenueResearch | null>(null);

  // Create research mutation
  const createResearchMutation = useMutation({
    mutationFn: (data: ResearchFormData) => VenueResearchService.createResearch(data),
    onSuccess: (result) => {
      const jobsPlural = result.totalJobs === 1 ? 'job' : 'jobs';
      const message = result.totalJobs === 1
        ? `Research job started! The AI agent will discover hotels matching your criteria.`
        : `${result.totalJobs} research ${jobsPlural} started! The AI agent will discover hotels across all cities and searches.`;
      
      showSuccess(message);
      // Invalidate research list to show the new jobs
      queryClient.invalidateQueries({ queryKey: ['venue-research'] });
    },
    onError: (error: Error) => {
      showError(`Failed to start research: ${error.message}`);
    },
  });

  const handleSubmit = async (data: ResearchFormData) => {
    await createResearchMutation.mutateAsync(data);
  };

  const handleViewDetails = (research: VenueResearch) => {
    setSelectedResearch(research);
  };

  const handleCloseDetails = () => {
    setSelectedResearch(null);
  };

  return (
    <PageContainer>
      <div className="mb-6">
        <Breadcrumb
          items={[
            { label: 'Venues', href: '/organization/venues' },
            { label: 'AI Research' },
          ]}
        />
      </div>
      <PageHeader
        title="AI Hotel Research"
        description="Discover hotels in any city using AI-powered web search"
      />

      <div className="space-y-6">
        {/* Research Form */}
        <ResearchForm
          onSubmit={handleSubmit}
          isSubmitting={createResearchMutation.isPending}
        />

        {/* Research History */}
        <ResearchHistoryTable onViewDetails={handleViewDetails} />
      </div>

      {/* Research Details Modal */}
      {selectedResearch && (
        <ResearchDetailsModal
          isOpen={!!selectedResearch}
          onClose={handleCloseDetails}
          researchId={selectedResearch.id}
        />
      )}
    </PageContainer>
  );
}
